import { create } from 'zustand';

import { dropKeys, hasKey, importHistoryKey, retainKey } from './cipher';

interface HistorySession {
  owner: string | null;
  /** Undefined until a lookup on this page has answered. */
  signedIn: boolean | undefined;
  /** Website auth only: signed in through the in-chat sign-in, standing in for the site's login. */
  inChat: boolean;
}

export const useHistorySession = create<HistorySession>(() => ({
  owner: null,
  signedIn: undefined,
  inChat: false,
}));
export type SessionNote = {
  signedIn?: boolean;
  scope?: string;
  historyKey?: string;
  inChat?: boolean;
} | null;
let generation = 0;

/** A newer answer invalidates pending imports before they can publish a key. */
export async function noteSession(body: SessionNote): Promise<void> {
  const current = ++generation;
  const signedIn = Boolean(body?.signedIn);
  const inChat = Boolean(body?.inChat);
  function publishOwner(owner: string | null) {
    publish({ owner, signedIn, inChat });
  }
  const next =
    body?.signedIn && body.scope && body.historyKey
      ? { owner: body.scope, historyKey: body.historyKey }
      : null;
  if (!next) {
    dropKeys();
    publishOwner(null);
    return;
  }
  const { owner, historyKey } = next;
  if (owner === useHistorySession.getState().owner && hasKey(owner)) {
    publishOwner(owner);
    return;
  }
  // Hide the old account while the replacement key is importing.
  dropKeys();
  publishOwner(null);
  try {
    const key = await importHistoryKey(historyKey);
    if (current !== generation) return;
    retainKey({ owner, key });
    publishOwner(owner);
  } catch (error) {
    if (current === generation) {
      dropKeys();
      publishOwner(null);
    }
    throw error;
  }
}

function publish(next: Pick<HistorySession, 'owner' | 'signedIn' | 'inChat'>): void {
  const current = useHistorySession.getState();
  if (
    current.owner !== next.owner ||
    current.signedIn !== next.signedIn ||
    current.inChat !== next.inChat
  )
    useHistorySession.setState(next);
}
